import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Printer } from 'lucide-react';
import byteLockup from '../assets/byte-lockup.png';
import { getAgreement } from '../api/adopt';
import { RichText } from '../components/RichText';
import { formatDateTime } from '../lib/format';

/** Authenticated, owned-only agreement snapshot the franchisee can print (PRD §16.4). No async PDF. */
export function AgreementPage() {
  const { linkId = '', seq = '1' } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['agreement', linkId, seq],
    queryFn: () => getAgreement(linkId, Number(seq)),
    retry: false,
  });

  if (isLoading)
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-graphite-400" /></div>;
  if (isError || !data)
    return <div className="flex min-h-screen items-center justify-center text-sm text-graphite-500">This agreement isn’t available.</div>;

  return (
    <div className="min-h-screen bg-surface-canvas">
      <div className="mx-auto max-w-3xl px-4 py-8">
        <div className="mb-4 flex items-center justify-between print:hidden">
          <img src={byteLockup} alt="Byte by Yum!" className="h-7 w-auto" />
          <button
            data-testid="print-agreement"
            onClick={() => window.print()}
            className="flex cursor-pointer items-center gap-2 rounded-lg border border-border-subtle bg-white px-3 py-1.5 text-sm text-graphite-700 shadow-xs transition hover:border-byte-purple/30 hover:bg-surface-warm/50 hover:text-graphite-900"
          >
            <Printer className="h-4 w-4" /> Print
          </button>
        </div>

        <article data-testid="agreement" className="rounded-2xl border border-border-subtle bg-white p-8 shadow-sm print:border-0 print:shadow-none">
          <h1 className="text-xl font-bold">Byte Product Adoption Agreement</h1>
          <p className="mt-1 text-sm text-graphite-500">Accepted {formatDateTime(data.acceptedAt)}</p>

          <dl className="mt-6 grid grid-cols-2 gap-3 text-sm">
            <div><dt className="text-graphite-400">Franchisee</dt><dd className="font-medium">{data.franchiseeName}</dd></div>
            <div><dt className="text-graphite-400">Market</dt><dd className="font-medium capitalize">{data.brand.replace(/-/g, ' ')} · {data.country?.toUpperCase()}</dd></div>
            <div><dt className="text-graphite-400">Signer</dt><dd className="font-medium">{data.signer.signerName}, {data.signer.signerTitle}</dd></div>
            <div><dt className="text-graphite-400">Stores (billing)</dt><dd className="font-medium">{data.storeCount} ({data.billingStoreCount})</dd></div>
          </dl>

          <h2 className="mt-8 text-sm font-semibold uppercase tracking-wide text-graphite-400">Adopted products</h2>
          <div className="mt-3 flex flex-col gap-4">
            {data.lines.map((l) => (
              <div key={l.productCode} className="border-t border-border-subtle pt-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{l.productName}</span>
                  <span className="text-sm text-graphite-600">
                    {l.unitPrice === 0 ? 'Included' : `$${l.unitPrice.toFixed(2)}`}
                    {l.billingType === 'per_store' ? ` × ${l.storeCount} = $${l.lineTotal.toFixed(2)}/mo` : ''}
                  </span>
                </div>
                <p className="text-xs text-graphite-400">
                  {l.msaRef && <>MSA {l.msaRef} · </>}{l.sowRef && <>SOW {l.sowRef} · </>}terms v{l.contentVersion}
                </p>
                <div className="mt-1 space-y-2 text-xs text-graphite-600">
                  {l.clauses.map((c) => (
                    <div key={c.id}>
                      <span className="font-medium text-graphite-700">{c.title}</span>
                      <RichText html={c.body} className="mt-0.5" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-8 break-all text-[10px] text-graphite-300">Content hash: {data.hash}</p>
        </article>
      </div>
    </div>
  );
}
